

import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn, Download, Share2 } from 'lucide-react';

const ImageViewer = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const modalRef = useRef(null);
  const imageRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch images from the backend API
  const fetchImages = async () => {
    setLoading(true); // Set loading state
    setError(null); // Reset error state

    try {
      const response = await fetch(`${API_BASE_URL}/api/images`);
      if (!response.ok) throw new Error('Failed to fetch images.');
      const data = await response.json();
      setImages(data); // Set images from the response
    } catch (error) {
      console.error('Error fetching images:', error);
      setError('Error fetching images. Please try again later.'); // Set error message
    } finally {
      setLoading(false); // Reset loading state
    }
  };

  useEffect(() => {
    fetchImages(); // Fetch images on component mount
  }, []);

  const openModal = (index) => {
    setSelectedImage(index);
    setIsModalOpen(true);
    setImageLoading(true);
    document.body.style.overflow = 'hidden';
    
    // Preload the image for smooth transition
    const img = new Image();
    img.onload = () => setImageLoading(false);
    img.src = images[index]?.src;
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedImage(null);
      document.body.style.overflow = 'auto';
    }, 300); // Wait for animation to complete
  };

  const navigate = (direction) => {
    setImageLoading(true);
    const newIndex = direction === 'next'
      ? (selectedImage + 1) % images.length
      : (selectedImage - 1 + images.length) % images.length;
    
    // Preload next image
    const img = new Image();
    img.onload = () => {
      setSelectedImage(newIndex);
      setImageLoading(false);
    };
    img.src = images[newIndex]?.src;
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) navigate('next');
    if (isRightSwipe) navigate('prev');
  };

  const handleDownload = () => {
    if (images[selectedImage]) {
      const link = document.createElement('a');
      link.href = images[selectedImage].src;
      link.download = `image-${selectedImage + 1}.jpg`;
      link.click();
    }
  };

  const handleShare = async () => {
    if (navigator.share && images[selectedImage]) {
      try {
        await navigator.share({
          title: 'Check out this image',
          url: images[selectedImage].src,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedImage === null) return;

      switch (e.key) {
        case 'ArrowLeft':
          navigate('prev');
          break;
        case 'ArrowRight':
          navigate('next');
          break;
        case 'Escape':
          closeModal();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-slate-500 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative  max-w-7xl mx-auto min-h-[90vh] h-auto p-8 before:absolute before:pointer-events-none">
      <h2 className="relative z-10 text-4xl font-bold text-center mb-12 bg-gradient-to-br from-white to-slate-300 bg-clip-text text-transparent">
        Gallery
      </h2>
      <div className="relative z-10 grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-8 md:grid-cols-[repeat(auto-fit,minmax(250px,1fr))] md:gap-6 sm:grid-cols-1">
        {images.length > 0 ? (
          images.map((image, index) => (
            <div
              key={image.id}
              className="group relative cursor-pointer rounded-2xl overflow-hidden bg-white/10 backdrop-blur-[20px] border border-white/20 shadow-[0_4px_20px_rgba(0,0,0,0.1)] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:transform hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_12px_40px_rgba(0,0,0,0.2)] active:transform active:-translate-y-1 active:scale-[0.98] active:shadow-[0_2px_10px_rgba(0,0,0,0.15)] active:transition-all active:duration-100 animate-[fadeInUp_0.4s_cubic-bezier(0.68,-0.55,0.265,1.55)_both] origin-center-bottom"
              onClick={() => openModal(index)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative w-full h-[300px] md:h-[250px] sm:h-[200px] overflow-hidden">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover block transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-black/70 backdrop-blur-[4px] flex items-center justify-center opacity-0 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] scale-80 group-hover:opacity-100 group-hover:scale-100">
                  <div className="flex flex-col items-center gap-2 transform translate-y-5 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-y-0">
                    <ZoomIn className="text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] animate-pulse" size={32} />
                    <span className="text-white text-sm font-medium text-shadow opacity-90">View Image</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-16 px-8 text-slate-500 text-lg">
            <p>No images available</p>
          </div>
        )}
      </div>

      {selectedImage !== null && (
        <div 
          className={`fixed inset-0 bg-black/95 backdrop-blur-[20px] flex items-center justify-center z-[1000] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
            isModalOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          ref={modalRef}
          onClick={(e) => e.target === modalRef.current && closeModal()}
        >
          <div className={`relative w-full h-full flex flex-col items-center justify-center p-8 md:p-4 ${
            isModalOpen 
              ? 'animate-[modalSlideIn_0.4s_cubic-bezier(0.68,-0.55,0.265,1.55)_ease-out]' 
              : 'animate-[modalSlideOut_0.3s_cubic-bezier(0.4,0,0.2,1)_ease-in]'
          }`}>
            {/* Top Controls */}
            <div className="absolute top-4 left-4 right-4 md:top-2 md:left-2 md:right-2 flex justify-between items-center z-[1100]">
              <div className="flex gap-2">
                <button
                  onClick={handleDownload}
                  className="bg-white/10 backdrop-blur-[20px] border border-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center cursor-pointer transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] opacity-80 hover:bg-white/20 hover:opacity-100 hover:scale-110"
                  aria-label="Download image"
                >
                  <Download size={20} />
                </button>
                <button
                  onClick={handleShare}
                  className="bg-white/10 backdrop-blur-[20px] border border-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center cursor-pointer transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] opacity-80 hover:bg-white/20 hover:opacity-100 hover:scale-110"
                  aria-label="Share image"
                >
                  <Share2 size={20} />
                </button>
              </div>
              <button
                onClick={closeModal}
                className="relative bg-white/10 backdrop-blur-[20px] border border-white/20 text-white rounded-full w-12 h-12 flex items-center justify-center cursor-pointer transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] opacity-80 hover:bg-red-500/20 hover:border-red-500/30 hover:opacity-100 hover:scale-110 hover:rotate-90"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={() => navigate('prev')}
              className="absolute top-1/2 left-4 md:left-2 -translate-y-1/2 bg-white/10 backdrop-blur-[20px] border border-white/20 text-white rounded-full w-12 h-12 md:w-10 md:h-10 flex items-center justify-center cursor-pointer z-[1100] transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] opacity-70 hover:bg-white/20 hover:opacity-100 hover:scale-110 active:-translate-y-1/2 active:scale-95"
              aria-label="Previous image"
            >
              <ChevronLeft size={32} />
            </button>

            <button
              onClick={() => navigate('next')}
              className="absolute top-1/2 right-4 md:right-2 -translate-y-1/2 bg-white/10 backdrop-blur-[20px] border border-white/20 text-white rounded-full w-12 h-12 md:w-10 md:h-10 flex items-center justify-center cursor-pointer z-[1100] transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] opacity-70 hover:bg-white/20 hover:opacity-100 hover:scale-110 active:-translate-y-1/2 active:scale-95"
              aria-label="Next image"
            >
              <ChevronRight size={32} />
            </button>

            {/* Image Container */}
            <div 
              className="relative flex items-center justify-center flex-1 w-full max-w-[90vw] max-h-[70vh] md:max-h-[60vh]"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {imageLoading && (
                <div className="absolute flex items-center justify-center z-10">
                  <div className="w-12 h-12 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
                </div>
              )}
              
              {images[selectedImage] && (
                <img
                  ref={imageRef}
                  src={images[selectedImage].src}
                  alt={images[selectedImage].alt}
                  className={`max-w-full max-h-full object-contain select-none rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.5)] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                    imageLoading ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
                  }`}
                  onLoad={() => setImageLoading(false)}
                />
              )}
            </div>

            {/* Bottom Info */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-[1100]">
              <div className="px-6 py-3 bg-white/10 backdrop-blur-[20px] border border-white/20 rounded-full text-white text-sm font-medium flex items-center gap-2 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                <span className="font-bold text-blue-400">{selectedImage + 1}</span>
                <span className="opacity-60">/</span>
                <span className="opacity-80">{images.length}</span>
              </div>
              {images[selectedImage]?.alt && (
                <div className="px-4 py-2 bg-black/80 rounded-lg text-white text-xs max-w-[300px] text-center opacity-90">
                  {images[selectedImage].alt}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
